import { useEffect, useRef, useState } from 'react';

const FFTSIZE = 256;
const AUDIO_SRC = '/audio.opus';

export function Waveform() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const waveShaperRef = useRef<WaveShaperNode | null>(null);
  const preGainRef = useRef<GainNode | null>(null);
  const postGainRef = useRef<GainNode | null>(null);
  const compressorRef = useRef<DynamicsCompressorNode | null>(null);
  const isHoveredRef = useRef<boolean>(false);

  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const isSeekingRef = useRef<boolean>(false);
  const [lastY, setLastY] = useState<number>(0);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [distortion, setDistortion] = useState<number>(0);
  const distortionRef = useRef<number>(0);
  const [isDragActive, setIsDragActive] = useState<boolean>(false);
  const dragCounterRef = useRef<number>(0);
  const objectUrlRef = useRef<string | null>(null);
  const [trackLabel, setTrackLabel] = useState<string>('Inferno - Yeule');
  const [isHover, setIsHover] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    isHoveredRef.current = isHovered;
  }, [isHovered]);

  useEffect(() => {
    if (!audioRef.current) return;
    const audioCtx = new AudioContext();
    audioContextRef.current = audioCtx;
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = FFTSIZE;
    analyserRef.current = analyser;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    dataArrayRef.current = dataArray;
  const source = audioCtx.createMediaElementSource(audioRef.current);

    const preGain = audioCtx.createGain();
    preGain.gain.value = 1;
    preGainRef.current = preGain;

    const waveShaper = audioCtx.createWaveShaper();
    waveShaper.oversample = '2x';
    waveShaper.curve = makeDistortionCurve(distortion);
    waveShaperRef.current = waveShaper;

    const postGain = audioCtx.createGain();
    postGain.gain.value = 0.12;
    postGainRef.current = postGain;

    const compressor = audioCtx.createDynamicsCompressor();
    compressor.threshold.value = -10;
    compressor.knee.value = 24;
    compressor.ratio.value = 12;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.25;
    compressorRef.current = compressor;

    source.connect(preGain);
    preGain.connect(waveShaper);
    waveShaper.connect(postGain);
    postGain.connect(compressor);
    compressor.connect(analyser);
    analyser.connect(audioCtx.destination);

    audioRef.current.onplay = () => {
      if (audioContextRef.current?.state === 'suspended')
        audioContextRef.current?.resume();
    };

    const onLoadedMetadata = () => {
      if (!audioRef.current) return;
      setDuration(audioRef.current.duration || 0);
    };
    const onTimeUpdate = () => {
      if (!audioRef.current) return;
      if (!isSeekingRef.current)
        setCurrentTime(audioRef.current.currentTime || 0);
    };
    const onEnded = () =>
      setCurrentTime((prev) => (duration ? duration : prev));

    audioRef.current.addEventListener('loadedmetadata', onLoadedMetadata);
    audioRef.current.addEventListener('timeupdate', onTimeUpdate);
    audioRef.current.addEventListener('ended', onEnded);

    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();

    draw();

    return () => {
      analyser.disconnect();
      if (compressorRef.current) compressorRef.current.disconnect();
      postGain.disconnect();
      waveShaper.disconnect();
      preGain.disconnect();
      source.disconnect();
      audioCtx.close();
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.removeEventListener(
          'loadedmetadata',
          onLoadedMetadata
        );
        audioRef.current.removeEventListener('timeupdate', onTimeUpdate);
        audioRef.current.removeEventListener('ended', onEnded);
      }
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  // Pull the whole track down in one request and play it from a blob URL.
  // The asset host does not serve range requests, so this avoids partial
  // fetches entirely: one download, then every seek is local to the buffer.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetch(AUDIO_SRC);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = url;
        if (audioRef.current) {
          audioRef.current.src = url;
          audioRef.current.load();
        }
      } catch {
        // Network hiccup: fall back to letting the element stream it itself.
        if (cancelled || !audioRef.current) return;
        audioRef.current.src = AUDIO_SRC;
        audioRef.current.load();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (waveShaperRef.current) {
      waveShaperRef.current.curve = makeDistortionCurve(distortion);
    }
    if (postGainRef.current && audioContextRef.current) {
      const k = Math.max(0, Math.min(150, distortion));
      const compensation = 1 / (1 + k / 40);
      const base = 0.6;
      const newGain = Math.max(0.15, base * compensation);
      try {
        postGainRef.current.gain.setTargetAtTime(
          newGain,
          audioContextRef.current.currentTime,
          0.01
        );
      } catch {
        postGainRef.current.gain.value = newGain;
      }
    }
  }, [distortion]);

  useEffect(() => {
    distortionRef.current = distortion;
  }, [distortion]);

  // isSeeking is tracked only via ref to avoid state/closure issues

  useEffect(() => {
    const t = setInterval(() => updateDistort(), 100);
    return () => clearInterval(t);
  }, []);

  const updateDistort = () => {
    const newY = window.scrollY;
    setLastY((prev) => {
      const scrollSpeed = Math.abs(newY - prev);
      if (isHoveredRef.current) setDistortion(20);
      else setDistortion(scrollSpeed * 5);
      return newY;
    });
  };

  const draw = () => {
    rafIdRef.current = requestAnimationFrame(draw);
    if (!analyserRef.current || !dataArrayRef.current || !canvasRef.current)
      return;
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;
    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext('2d');
    if (!canvasCtx) return;

    const WIDTH = canvas.width;
    const HEIGHT = canvas.height;
    analyser.getByteTimeDomainData(dataArray);
    canvasCtx.fillStyle = '#141414';
    canvasCtx.fillRect(0, 0, WIDTH, HEIGHT);
    canvasCtx.lineWidth = 2;
    canvasCtx.strokeStyle = '#e13131';
    canvasCtx.beginPath();

    const sliceWidth = (WIDTH * 1.0) / dataArray.length;
    let x = 0;
    for (let u of dataArray) {
      const v = ((u - 126) / (128 * 0.35)) * HEIGHT;
      const y = HEIGHT - 2;
      if (x === 0) {
        canvasCtx.moveTo(x, y);
      } else {
        const d = distortionRef.current;
        const divisor = d > 0 ? 64 : 128;
        const a = Math.max(0.1, (v * 2) / divisor);
        const colour =
          d > 0 ? `rgba(225, 49, 49, ${a})` : `rgba(200, 200, 200, ${a})`;
        canvasCtx.fillStyle = colour;
        canvasCtx.fillRect(x, y, 2, -v);
      }
      x += sliceWidth;
    }
    canvasCtx.stroke();
  };

  const resizeCanvas = () => {
    if (canvasRef.current) {
      const w = document.getElementById('waveform')?.clientWidth;
      if (w) canvasRef.current.width = w;
      else canvasRef.current.width = window.innerWidth * 0.6 - 36;
      canvasRef.current.height = 100;
    }
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (audioRef.current.paused) audioRef.current.play();
      else audioRef.current.pause();
    }

    if (audioContextRef.current?.state === 'suspended')
      audioContextRef.current?.resume();
  };

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60)
      .toString()
      .padStart(2, '0');
    return `${m}:${s}`;
  };

  const onSliderChange = (value: number) => {
    setCurrentTime(value);
    if (audioRef.current) audioRef.current.currentTime = value;
  };

  const makeDistortionCurve = (amount: number) => {
    const k =
      typeof amount === 'number' ? Math.max(0, Math.min(150, amount)) : 0;
    const nSamples = 44100;
    const curve = new Float32Array(nSamples);
    const deg = Math.PI / 180;
    let x = 0;
    for (let i = 0; i < nSamples; ++i) {
      x = (i * 2) / nSamples - 1;
      curve[i] = ((3 + k) * x * 20 * deg) / (Math.PI + k * Math.abs(x));
    }
    return curve;
  };

  const onDragEnter: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    setIsDragActive(true);
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };

  const onDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };

  const onDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragActive(false);
  };

  const onUpload = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/*';
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      if (!target.files || target.files.length === 0) return;
      const file = target.files[0];
      if (!file) return;
      if (file.type && !file.type.startsWith('audio/')) return;
      const url = URL.createObjectURL(file);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = url;
      setTrackLabel(file.name || 'Custom audio');
      if (audioRef.current) {
        audioRef.current.src = url;
        try {
          audioRef.current.currentTime = 0;
        } catch {}
        audioRef.current.load();
        const play = async () => {
          if (audioContextRef.current?.state === 'suspended')
            await audioContextRef.current.resume();
          await audioRef.current?.play();
        };
        play();
      }
    };
    input.click();
  };

  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragActive(false);

    const dt = e.dataTransfer;
    if (!dt) return;
    let file: File | null = null;
    if (dt.files && dt.files.length > 0) {
      const files = Array.from(dt.files);
      const candidate = files.find((f) => f.type.startsWith('audio/'));
      file = candidate ?? files[0] ?? null;
    }
    if (!file) return;
    if (file.type && !file.type.startsWith('audio/')) return;

    const url = URL.createObjectURL(file);
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = url;
    setTrackLabel(file.name || 'Custom audio');

    if (audioRef.current) {
      audioRef.current.src = url;
      try {
        audioRef.current.currentTime = 0;
      } catch {}
      audioRef.current.load();
      const play = async () => {
        if (audioContextRef.current?.state === 'suspended')
          await audioContextRef.current.resume();
        await audioRef.current?.play();
      };
      void play();
    }
  };

  return (
    <div
      onClick={togglePlay}
      className={`cursor-pointer select-none border-2 border-transparent ${
        isDragActive ? 'border-dashed border-red-400/60!' : ''
      }`}
      id="waveform"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <audio controls ref={audioRef} className="hidden" preload="auto">
        Your browser does not support the audio element.
      </audio>
      <canvas
        width={400}
        height={100}
        ref={canvasRef}
        className="block w-full h-[100px]"
      ></canvas>
      <div className="mt-2">
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 tabular-nums w-10 text-right">
            {formatTime(currentTime)}
          </span>
          <input
            aria-label="Seek through audio"
            type="range"
            min={0}
            max={duration || 0}
            step={0.01}
            value={Math.min(currentTime, duration || 0)}
            onChange={(e) => onSliderChange(Number(e.target.value))}
            onMouseDown={(e) => {
              e.stopPropagation();
              isSeekingRef.current = true;
            }}
            onMouseUp={(e) => {
              e.stopPropagation();
              isSeekingRef.current = false;
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              isSeekingRef.current = true;
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              isSeekingRef.current = false;
            }}
            onClick={(e) => e.stopPropagation()}
            disabled={!duration}
            className="min-w-0 flex-1 h-1 rounded-lg appearance-none bg-gray-800 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/50 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-red-400 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:border-0 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-red-400 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
          />
          <span className="text-xs text-gray-500 tabular-nums w-10">
            {formatTime(duration)}
          </span>
        </div>
        <div
          className="my-2 px-3"
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
          onClick={onUpload}
        >
          <span className="text-sm text-gray-500">
            {isHover ? 'Change the audio' : isLoading ? 'Loading…' : trackLabel}
          </span>
          {isDragActive && (
            <span className="ml-5 text-red-300">Drop audio to play</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default Waveform;
